import assertNever from '../../utils/assertNever';
import { BadInputError } from '../../errors/BadInputError';

import { ASTCall } from '../ast';
import { Context, Func, SqlExpression } from '../types';
import { ExprType, stringifyType } from '../expr_types';
import {
  assertKwargShape,
  typecheckExpression,
  isSameType,
  TypecheckWarning,
} from '../typecheck';
import { evalExpression } from '../eval';
import { exprToSql } from '../sql';
import { safeMaxStage } from '../utils';
import { mergeErrors } from '../sql/internal';

// Function for creating a list of primitive values. Syntax is:
// list(1, 2, 3)
//
// The list must not be empty and may only contain primitive values of the same
// type. This means that you can have a list of numbers or a list of strings but
// NOT a list of structs, a list of list, etc
export const list: Func = {
  typecheck(
    ctx: Context,
    call: ASTCall,
    warnings: Array<TypecheckWarning>
  ): ExprType {
    if (call.args.length < 1) {
      throw new BadInputError(
        'list function must be invoked with at least one argument'
      );
    }
    assertKwargShape(call, [], false, undefined, undefined);

    // Check all elements have the same type.
    const allElemTypes = call.args.map((arg) =>
      typecheckExpression(ctx, arg, warnings)
    );
    const elemTy = allElemTypes[0];
    const diffTy = allElemTypes.find((ty) => !isSameType(elemTy, ty));
    if (diffTy !== undefined) {
      throw new BadInputError(
        `list may only contain elements of a single type, got ${stringifyType(
          elemTy
        )} and ${stringifyType(diffTy)}`
      );
    }

    // Check element is of a primitive type.
    switch (elemTy.ty) {
      case 'number':
      case 'string':
      case 'boolean':
        return { ty: 'list', elemTy };
      case 'list':
      case 'quantity':
      case 'date':
      case 'datetime':
      case 'nullable':
      case 'null':
        throw new BadInputError(
          `list may only contain number, string, or boolean values, got ${stringifyType(
            elemTy
          )}`
        );

      default:
        assertNever(elemTy);
    }
  },

  eval(ctx: Context, call: ASTCall): unknown {
    return call.args.map((arg) => evalExpression(ctx, arg));
  },

  toSql(ctx: Context, call: ASTCall): SqlExpression {
    const argsSql = call.args.map((arg) => exprToSql(ctx, arg));
    return {
      expr: `[${argsSql.map((sql) => sql.expr).join(',')}]`,
      stage: safeMaxStage(argsSql),
      unitExpr: null,
      error: mergeErrors(argsSql.map((e) => e.error)),
      trace: null,
    };
  },
};
